import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Stack,
  Link as MuiLink,
  AlertTitle,
  Tabs,
  Tab,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper as MuiPaper
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Login as LoginIcon, 
  Google as GoogleIcon, 
  Email as EmailIcon,
  PersonAdd as RegisterIcon,
  Psychology,
  Star,
  WorkOutline,
  Email,
  EmojiEvents
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { useTheme } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#757575',
  borderColor: '#ddd',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#ccc',
  },
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2),
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(true);
  
  const { 
    currentUser, 
    isPremiumUser, 
    loginWithGoogle, 
    loginWithEmail,
    createAccount,
    loading: authLoading 
  } = useAuth();
  
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  
  const isLoggedIn = !!currentUser;

  // Check for tab parameter in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'register') {
      setTabValue(1); // Set to registration tab
    }
  }, []);

  useEffect(() => {
    if (!authLoading && currentUser) {
      console.log("LoginPage: Auth loaded, user logged in. Checking role...");
      if (currentUser.role === 'coach') {
        console.log("User is a coach, redirecting to /coach");
        navigate('/coach');
      } else {
        console.log("User is not a coach, redirecting to /afspraak");
        navigate('/afspraak');
      }
    } else if (!authLoading) {
      console.log("LoginPage: Auth loaded, no user logged in.");
    }
  }, [authLoading, currentUser, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!loginEmail || !loginPassword) {
      setError('Voer zowel e-mail als wachtwoord in.');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginEmail)) {
      setError('Voer een geldig e-mailadres in.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const loginSuccess = await loginWithEmail(loginEmail, loginPassword);
      
      if (loginSuccess) {
        // Don't set success message here, rely on useEffect to redirect
      } else {
        setError('Ongeldige inloggegevens. Controleer je e-mail en wachtwoord.');
      }
    } catch (err: any) {
       console.error("Login Submit Error:", err);
       if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
            setError('Ongeldige inloggegevens. Controleer je e-mail en wachtwoord.');
        } else {
            setError('Er is een fout opgetreden bij het inloggen. Probeer het later opnieuw.');
        }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!registerEmail || !registerPassword || !registerName) {
      setError('Vul alstublieft alle verplichte velden in.');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerEmail)) {
      setError('Voer een geldig e-mailadres in.');
      return;
    }
    
    if (registerPassword.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const registerSuccess = await createAccount(registerEmail, registerPassword, registerName);
      
      if (registerSuccess) {
         // Don't set success message here, rely on useEffect to redirect
      } else {
        setError('Registratie mislukt. Mogelijk is dit e-mailadres al in gebruik.'); 
      }
    } catch (err: any) {
      console.error("Register Submit Error:", err);
       if (err.code === 'auth/email-already-in-use') {
           setError('Dit e-mailadres is al in gebruik. Probeer in te loggen.');
       } else if (err.code === 'auth/weak-password' || err.code === 'auth/password-does-not-meet-requirements') {
           let specificRequirement = "Controleer de wachtwoordeisen.";
           if (err.message) {
               if (err.message.includes("upper case")) {
                   specificRequirement = "Wachtwoord moet een hoofdletter bevatten.";
               } else if (err.message.includes("6 characters")) {
                   specificRequirement = "Wachtwoord moet minimaal 6 tekens lang zijn.";
               } 
           }
           setError(`Wachtwoord voldoet niet aan de eisen. ${specificRequirement}`);
       } else {
          setError('Er is een fout opgetreden bij het aanmaken van je account. Probeer het later opnieuw.');
       }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    
    try {
      const googleSuccess = await loginWithGoogle();
      
      if (googleSuccess) {
         // Don't set success message here, rely on useEffect to redirect
      } else {
        setError('Er is een probleem opgetreden bij het inloggen met Google. Probeer het later opnieuw.');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError('Er is een fout opgetreden bij het inloggen met Google.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLoginForm = () => (
    <Box component="form" onSubmit={handleLoginSubmit} noValidate>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label={language === 'nl' ? "E-mailadres" : "Email Address"}
        name="email"
        autoComplete="email"
        autoFocus
        value={loginEmail}
        onChange={(e) => setLoginEmail(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label={language === 'nl' ? "Wachtwoord" : "Password"}
        type={showPassword ? 'text' : 'password'}
        id="password"
        autoComplete="current-password"
        value={loginPassword}
        onChange={(e) => setLoginPassword(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Box></Box>
        <Button 
          component={RouterLink} 
          to="/wachtwoord-vergeten" 
          variant="text" 
          color="primary"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          {language === 'nl' ? "Wachtwoord vergeten?" : "Forgot password?"}
        </Button>
      </Box>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        sx={{ mt: 3, mb: 2, py: 1.5 }}
        disabled={isSubmitting}
        startIcon={<LoginIcon />}
      >
        {isSubmitting ? (
          <CircularProgress size={24} />
        ) : (
          language === 'nl' ? "Inloggen" : "Sign In"
        )}
      </Button>
      <Typography variant="body2" align="center">
        {language === 'nl' ? "Nog geen account? " : "Don't have an account? "}
        <MuiLink 
          component="button" 
          variant="body2"
          onClick={() => setTabValue(1)}
        >
          {language === 'nl' ? "Registreer hier" : "Register here"}
        </MuiLink>
      </Typography>
    </Box>
  );

  const renderRegisterForm = () => (
    <Box component="form" onSubmit={handleRegisterSubmit} noValidate>
      <TextField
        margin="normal"
        required
        fullWidth
        id="name"
        label={language === 'nl' ? "Volledige naam" : "Full Name"}
        name="name"
        autoComplete="name"
        autoFocus
        value={registerName}
        onChange={(e) => setRegisterName(e.target.value)}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        id="registerEmail"
        label={language === 'nl' ? "E-mailadres" : "Email Address"}
        name="email"
        autoComplete="email"
        value={registerEmail}
        onChange={(e) => setRegisterEmail(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label={language === 'nl' ? "Wachtwoord" : "Password"}
        type={showPassword ? 'text' : 'password'}
        id="registerPassword"
        autoComplete="new-password"
        value={registerPassword}
        onChange={(e) => setRegisterPassword(e.target.value)}
        helperText={language === 'nl' ? "Minimaal 6 tekens" : "Minimum 6 characters"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="secondary"
        sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 'bold' }}
        disabled={isSubmitting}
        startIcon={<RegisterIcon />}
      >
        {isSubmitting ? (
          <CircularProgress size={24} />
        ) : (
          language === 'nl' ? "Account aanmaken" : "Create Account"
        )}
      </Button>
      <Typography variant="body2" align="center" sx={{ mt: 1, color: 'text.secondary' }}>
        {language === 'nl' 
          ? "Door een account aan te maken, ga je akkoord met onze " 
          : "By creating an account, you agree to our "}
        <MuiLink component={RouterLink} to="/voorwaarden">
          {language === 'nl' ? "Algemene Voorwaarden" : "Terms of Service"}
        </MuiLink>
        {language === 'nl' ? " en " : " and "}
        <MuiLink component={RouterLink} to="/privacy">
          {language === 'nl' ? "Privacybeleid" : "Privacy Policy"}
        </MuiLink>.
      </Typography>
      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        {language === 'nl' ? "Heb je al een account? " : "Already have an account? "}
        <MuiLink 
          component="button" 
          variant="body2"
          onClick={() => setTabValue(0)}
        >
          {language === 'nl' ? "Log hier in" : "Sign in here"}
        </MuiLink>
      </Typography>
    </Box>
  );

  if (authLoading) {
      return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 132px)' }}>
              <CircularProgress />
          </Box>
      );
  }

  return (
    <Box sx={{ 
      minHeight: 'calc(100vh - 64px)', // Assuming 64px is header height
      bgcolor: 'background.default',
      display: 'flex',
      alignItems: 'center', // Vertically center content
      py: { xs: 4, md: 6 }
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <StyledPaper sx={{ height: '100%' }}> {/* Make paper fill height */} 
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              <Box sx={{ mb: 3 }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  centered
                  indicatorColor="secondary" // Use secondary for indicator
                  textColor="secondary" // Use secondary for text
                  sx={{ 
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: 1.5,
                    },
                  }}
                >
                  <Tab 
                    label={language === 'nl' ? "Inloggen" : "Sign In"} 
                    {...a11yProps(0)}
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      textTransform: 'none',
                      minWidth: '120px',
                    }}
                  />
                  <Tab 
                    label={language === 'nl' ? "Registreren" : "Sign Up"} 
                    {...a11yProps(1)}
                    sx={{ 
                      fontSize: '1rem', 
                      fontWeight: 500,
                      textTransform: 'none',
                      minWidth: '120px',
                    }}
                  />
                </Tabs>
              </Box>

              {showEmailLogin ? (
                <>
                  <TabPanel value={tabValue} index={0}>
                    {renderLoginForm()}
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    {renderRegisterForm()}
                  </TabPanel>
                </>
              ) : (
                <Box>
                  <GoogleButton
                    fullWidth
                    startIcon={<GoogleIcon />}
                    onClick={handleGoogleLogin}
                    variant="outlined"
                    size="large"
                  >
                    {language === 'nl' ? "Inloggen met Google" : "Sign in with Google"}
                  </GoogleButton>
                  <Box sx={{ textAlign: 'center', my: 2 }}>
                    <Button 
                      variant="text" 
                      color="primary" 
                      onClick={() => setShowEmailLogin(true)}
                    >
                      {language === 'nl' ? "Inloggen met e-mail" : "Sign in with email"}
                    </Button>
                  </Box>
                </Box>
              )}
              
              {showEmailLogin && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }}>{language === 'nl' ? "OF" : "OR"}</Divider>
                  <GoogleButton
                    fullWidth
                    startIcon={<GoogleIcon />}
                    onClick={handleGoogleLogin}
                    variant="outlined"
                    size="large"
                  >
                    {tabValue === 0 ? 
                      (language === 'nl' ? "Inloggen met Google" : "Sign in with Google") : 
                      (language === 'nl' ? "Registreren met Google" : "Sign up with Google")
                    }
                  </GoogleButton>
                </Box>
              )}
            </StyledPaper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              pl: { md: 5 }, 
              mt: { xs: 4, md: 0 },
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%'
            }}>
              <Typography variant="h4" component="h2" gutterBottom color="primary.main" fontWeight="bold">
                {language === 'nl' ? "Jouw Groei Start Hier" : "Your Growth Starts Here"}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {language === 'nl' 
                  ? "Maak een account aan en zet de eerste stap naar een carrière vol voldoening. Ontdek exclusieve tools en inzichten, gebaseerd op bewezen coachingmethoden, die je helpen jouw unieke pad te vinden en te volgen."
                  : "Create an account and take the first step towards a fulfilling career. Discover exclusive tools and insights, based on proven coaching methods, that help you find and follow your unique path."}
              </Typography>
              
              <List sx={{ mb: 3 }}>
                {[
                  {
                    title: language === 'nl' ? "Persoonlijke groei" : "Personal growth",
                    desc: language === 'nl' 
                      ? "Toegang tot gepersonaliseerde coaching materialen voor jouw carrière" 
                      : "Access to personalized coaching materials for your career",
                    icon: <Psychology color="secondary" sx={{ fontSize: 30 }} />
                  },
                  {
                    title: language === 'nl' ? "Premium content" : "Premium content",
                    desc: language === 'nl' 
                      ? "Ontgrendel exclusieve tools om je carrièredoelen te bereiken" 
                      : "Unlock exclusive tools to achieve your career goals",
                    icon: <Star color="secondary" sx={{ fontSize: 30 }} />
                  },
                  {
                    title: language === 'nl' ? "Carrière ontwikkeling" : "Career development",
                    desc: language === 'nl' 
                      ? "Professionele templates en stappenplannen voor je carrière" 
                      : "Professional templates and roadmaps for your career",
                    icon: <WorkOutline color="secondary" sx={{ fontSize: 30 }} />
                  },
                ].map((item, index) => (
                  <ListItem key={index} sx={{ alignItems: 'flex-start', py: 1.5, borderBottom: index < 2 ? `1px dashed ${theme.palette.divider}` : 'none' }}>
                    <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.desc}
                      primaryTypographyProps={{ fontWeight: 'bold', color: 'text.primary' }}
                      secondaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Paper 
                elevation={0}
                sx={{ 
                  mt: 'auto',
                  p: 3, 
                  borderRadius: 2,
                  bgcolor: 'secondary.light',
                  color: 'white',
                  border: '1px solid',
                  borderColor: 'secondary.main'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmojiEvents sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {language === 'nl' ? "Ontdek Premium Coaching" : "Discover Premium Coaching"}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {language === 'nl' 
                    ? "Klaar voor diepgaande begeleiding? Bekijk de premium pakketten voor persoonlijke coaching en exclusieve materialen."
                    : "Ready for in-depth guidance? View the premium packages for personal coaching and exclusive materials."}
                </Typography>
                <Button 
                  variant="contained"
                  color="secondary" 
                  fullWidth
                  onClick={() => navigate('/diensten/zelfcoaching?scrollTo=pakketten')}
                  sx={{ 
                    fontWeight: 'bold',
                    bgcolor: 'white',
                    color: 'secondary.main',
                    '&:hover': {
                      bgcolor: theme.palette.grey[100]
                    }
                  }}
                >
                  {language === 'nl' ? "Bekijk premium opties" : "View premium options"}
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage; 